import React, { useState, useEffect, useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Icon, Loader } from '@common/components';
import {
  faChevronRight,
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
} from '@fortawesome/free-solid-svg-icons';
import {
  useSyncHolesQuery,
  useLazyExportSyncHolesQuery,
} from '@dashboard/api/syncMonitor';
import TagBadge, {
  CenterStatusBadge,
  HistoricSinceBadge,
} from '@dashboard/components/SyncMonitor/TagBadge/TagBadge';
import CopyId from '@dashboard/components/SyncMonitor/CopyId';
import { isSelectingText, onActivateKey } from '@dashboard/components/SyncMonitor/rowSelection';
import DeliveryModeBadge from '@dashboard/components/SyncMonitor/DeliveryModeBadge';
import DayDetailBody from '@dashboard/components/SyncMonitor/DayDetailBody';
import classes from './HolesPanel.module.scss';

const SOURCE_OPTIONS = ['both', 'db', 's3'];
const TYPE_OPTIONS = ['any', 'usage', 'benchmark'];
const LIMIT = 50;

function Select({ label, value, options, prefix, onChange, t }) {
  const id = useId();
  return (
    <div className={classes.field}>
      <label htmlFor={id} className={classes.fieldLabel}>
        {label}
      </label>
      <select
        id={id}
        className={classes.select}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {t(`${prefix}_${o}`)}
          </option>
        ))}
      </select>
    </div>
  );
}
Select.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  prefix: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export function Pager({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  return (
    <div className={classes.pager}>
      <button
        type="button"
        className={classes.pagerBtn}
        disabled={page <= 1}
        onClick={() => onPage(1)}
        aria-label="First page"
      >
        <Icon icon={faAnglesLeft} />
      </button>
      <button
        type="button"
        className={classes.pagerBtn}
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        aria-label="Previous page"
      >
        <Icon icon={faAngleLeft} />
      </button>
      <span className={classes.pagerLabel}>{`${page} / ${totalPages}`}</span>
      <button
        type="button"
        className={classes.pagerBtn}
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
        aria-label="Next page"
      >
        <Icon icon={faAngleRight} />
      </button>
      <button
        type="button"
        className={classes.pagerBtn}
        disabled={page >= totalPages}
        onClick={() => onPage(totalPages)}
        aria-label="Last page"
      >
        <Icon icon={faAnglesRight} />
      </button>
    </div>
  );
}
Pager.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPage: PropTypes.func.isRequired,
};

export default function HolesPanel({ year, month, centerIds, tags }) {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });

  const [source, setSource] = useState('both');
  const [type, setType] = useState('any');
  const [group, setGroup] = useState('days');
  const [page, setPage] = useState(1);
  const [expandedKey, setExpandedKey] = useState(null);

  useEffect(() => {
    setPage(1);
    setExpandedKey(null);
  }, [year, month, centerIds.join(','), tags.join(','), source, type, group]);

  const {
    data: resp,
    isFetching,
    isError,
  } = useSyncHolesQuery({
    year,
    month,
    centerIds,
    tags,
    source,
    type,
    group,
    page,
    limit: LIMIT,
  });

  const [exportCsv, { isFetching: isExporting }] =
    useLazyExportSyncHolesQuery();
  const [exportFailed, setExportFailed] = useState(false);

  const handleExport = useCallback(async () => {
    setExportFailed(false);
    const result = await exportCsv({
      year,
      month,
      centerIds,
      tags,
      source,
      type,
    });
    if (result.isError || typeof result.data !== 'string') {
      setExportFailed(true);
      return;
    }
    const url = URL.createObjectURL(
      new Blob([result.data], { type: 'text/csv;charset=utf-8' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `sync-holes-${year}${
      month ? `-${String(month).padStart(2, '0')}` : ''
    }.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [exportCsv, year, month, centerIds, tags, source, type]);

  const toggle = useCallback(
    (key) => setExpandedKey((k) => (k === key ? null : key)),
    [],
  );

  const data = resp?.data;
  const rows = data?.rows ?? [];
  const meta = data?.meta ?? { total: 0, page: 1, total_pages: 1 };
  const totals = data?.totals;
  const remote = data?.remote;
  const sourceDegraded = data && data.source_used !== source;

  const body = () => {
    if (isFetching)
      return (
        <div className={classes.loaderWrap}>
          <Loader />
        </div>
      );
    if (isError) return <p className={classes.empty}>{t('holesLoadError')}</p>;
    if (data && !data.ready)
      return <p className={classes.empty}>{t('holesNotReady')}</p>;
    if (rows.length === 0)
      return <p className={classes.empty}>{t('holesEmpty')}</p>;

    if (group === 'centers') {
      return (
        <table className={classes.table}>
          <thead>
            <tr>
              <th>{t('holesColCenter')}</th>
              <th>{t('holesColFreshness')}</th>
              <th>{t('holesColHoles')}</th>
              <th>{t('holesColDb')}</th>
              <th>{t('holesColS3')}</th>
              <th>{t('holesColPartial')}</th>
              <th>{t('holesColFirst')}</th>
              <th>{t('holesColLast')}</th>
              <th>{t('holesColLastData')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.center_id}>
                <td>
                  {r.center_name}
                  <span className={classes.ext}> · <CopyId value={r.center_id} /></span>
                </td>
                <td>
                  <CenterStatusBadge status={r.center_status} />
                  {(r.tags || []).map((tag) => (
                    <TagBadge key={tag} tag={tag} />
                  ))}
                  <HistoricSinceBadge date={r.historic_since} />
                  {r.suitcases_count > 1 && (
                    <span
                      className={`${classes.badge} ${classes.suitcaseBadge}`}
                      title={t('holesSuitcaseOf', { count: r.suitcases_count })}
                    >
                      {t('holesSuitcaseCount', { count: r.suitcases_count })}
                    </span>
                  )}
                </td>
                <td>
                  <strong>{r.holes}</strong>
                </td>
                <td>{r.db_holes}</td>
                <td>{r.s3_holes}</td>
                <td>{r.partial_holes}</td>
                <td className={classes.mono}>{r.first_hole}</td>
                <td className={classes.mono}>{r.last_hole}</td>
                <td className={classes.mono}>{r.last_data_date ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return (
      <ul className={classes.list}>
        {rows.map((r) => {
          const key = `${r.center_id}|${r.date}`;
          const open = expandedKey === key;
          return (
            <li key={key}>
              <div
                role="button"
                tabIndex={0}
                className={classes.row}
                onClick={() => !isSelectingText() && toggle(key)}
                onKeyDown={onActivateKey(() => toggle(key))}
                aria-expanded={open}
              >
                <Icon
                  icon={faChevronRight}
                  className={`${classes.chevron} ${
                    open ? classes.chevronOpen : ''
                  }`}
                />
                <span className={classes.date}>{r.date}</span>
                <span className={classes.centerName}>
                  {r.center_name}
                  <span className={classes.ext}> · <CopyId value={r.center_id} /></span>
                </span>
                <CenterStatusBadge status={r.center_status} />
                {(r.tags || []).map((tag) => (
                  <TagBadge key={tag} tag={tag} />
                ))}
                <HistoricSinceBadge date={r.historic_since} />
                {r.suitcases_count > 1 && (
                  <span
                    className={`${classes.badge}`}
                    title={t('holesSuitcaseOf', { count: r.suitcases_count })}
                  >
                    {t('holesSuitcaseCount', { count: r.suitcases_count })}
                  </span>
                )}
                <span
                  className={`${classes.badge} ${classes[`kind_${r.kind}`]}`}
                >
                  {t(`holesKind_${r.kind}`)}
                </span>
                {r.delivery_mode ? (
                  <DeliveryModeBadge mode={r.delivery_mode} size="xs" />
                ) : null}
              </div>
              {open && r.center_uuid && (
                <div className={classes.accordion}>
                  <DayDetailBody centerId={r.center_uuid} date={r.date} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <h3 className={classes.title}>
          {month
            ? t('holesTitleMonth', { year, month: String(month).padStart(2, '0') })
            : t('holesTitle', { year })}
          {totals && (
            <span className={classes.count}>
              {group === 'centers'
                ? t('holesCountCenters', { count: totals.centers })
                : t('holesCountDays', { count: totals.rows })}
            </span>
          )}
        </h3>
        <div className={classes.headerActions}>
          <button
            type="button"
            className={classes.exportBtn}
            disabled={isExporting || !data?.ready}
            onClick={handleExport}
          >
            {isExporting ? '…' : t('holesExport')}
          </button>
          <Pager
            page={meta.page || 1}
            totalPages={meta.total_pages || 1}
            onPage={setPage}
          />
        </div>
      </div>
      {exportFailed && <p className={classes.warn}>{t('holesExportFailed')}</p>}

      <div className={classes.filters}>
        <Select
          label={t('holesFilterSource')}
          value={source}
          options={SOURCE_OPTIONS}
          prefix="holesSource"
          onChange={setSource}
          t={t}
        />
        <Select
          label={t('holesFilterType')}
          value={type}
          options={TYPE_OPTIONS}
          prefix="holesType"
          onChange={setType}
          t={t}
        />
        <Select
          label={t('holesFilterGroup')}
          value={group}
          options={['days', 'centers']}
          prefix="holesGroup"
          onChange={setGroup}
          t={t}
        />
      </div>

      {totals && data.ready && (
        <div className={classes.summary}>
          <span>{t('holesSummaryDb', { count: totals.db_holes })}</span>
          {data.source_used !== 'db' && (
            <span>{t('holesSummaryS3', { count: totals.s3_holes })}</span>
          )}
          {data.source_used !== 'db' && (
            <span>{t('holesSummaryBoth', { count: totals.both })}</span>
          )}
          {data.source_used !== 's3' && (
            <span>{t('holesSummaryPartial', { count: totals.partial })}</span>
          )}
        </div>
      )}

      {sourceDegraded && (
        <p className={classes.warn}>
          {t('holesSourceDegraded', {
            reason: t(
              `holesDegraded_${remote?.reason || 'range'}`,
              t('holesDegraded_range'),
            ),
          })}
        </p>
      )}
      {!sourceDegraded && remote?.available && data?.source_used !== 'db' && (
        <p className={classes.hint}>
          {t('remoteAsOf', {
            date: (remote.last_success_at || '').replace('T', ' ').slice(0, 16),
          })}
        </p>
      )}
      {type !== 'any' && <p className={classes.hint}>{t('holesTypeHint')}</p>}

      {body()}
    </div>
  );
}

HolesPanel.propTypes = {
  year: PropTypes.number.isRequired,
  centerIds: PropTypes.arrayOf(PropTypes.string),
  month: PropTypes.number,
  tags: PropTypes.arrayOf(PropTypes.string),
};

HolesPanel.defaultProps = {
  centerIds: [],
  month: null,
  tags: [],
};
